import { useSelector } from 'react-redux';

import { Roles } from 'app/auth';
import { DEPLOYMENT_REGIONS, deploymentRegion } from 'app/constants';
import useLoggedInUser from 'app/hooks/useLoggedInUser';
import useOrganisationFeatures, { type OrganisationPlan } from 'app/hooks/useOrganisationFeatures';
import { useModalDismissedState } from 'app/shared-components/modals';
import type { RootState } from 'app/store';
import newPlansSegment, { type NewPlansSegment } from 'app/utils/newPlansSegment';
import { NEW_PLAN_FEATURES_SEEN_KEY } from 'app/utils/seenStorageKeys';

type NewPlanFeaturesPopUp =
  | { status: 'loading' }
  | { status: 'hidden' }
  | { status: 'show'; segment: NewPlansSegment; plan: OrganisationPlan; dismiss: () => void };

/** Per user and organisation: someone in several organisations hears what each one gained. */
export function newPlanFeaturesSeenKey(userId: string, organisationId: string): string {
  return `${NEW_PLAN_FEATURES_SEEN_KEY}-${userId}-${organisationId}`;
}

/**
 * Tells the customers the move to the new plans gave features to what they gained: once per user,
 * the first time they open Connect after it launched. Only UK customers moved onto the new plans.
 * Support staff don't get it, as it isn't their plan.
 */
export default function useNewPlanFeaturesPopUp(): NewPlanFeaturesPopUp {
  const user = useLoggedInUser();
  const { plan, pricingLaunched, isLoading } = useOrganisationFeatures();
  const selectedPharmacy = useSelector(
    (state: RootState) => state.pharmacySelector.selectedPharmacy
  );
  const isPro = selectedPharmacy?.chain?.pro?.enabled === true;
  const { showOnboarding, dismiss } = useModalDismissedState(
    newPlanFeaturesSeenKey(user?._id ?? 'guest', selectedPharmacy?.chain_id ?? 'none')
  );

  if (
    !user ||
    user.roles.includes(Roles.SUPPORT) ||
    !showOnboarding ||
    deploymentRegion !== DEPLOYMENT_REGIONS.UK
  ) {
    return { status: 'hidden' };
  }
  if (isLoading) return { status: 'loading' };

  const segment = pricingLaunched ? newPlansSegment(plan, isPro) : null;
  if (!segment || !plan) return { status: 'hidden' };

  return { status: 'show', segment, plan, dismiss };
}
